import React, { useState } from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { HelpCircle, X } from 'lucide-react';

const FIELD_TIPS = {
  investment_name: "Give your investment a name you'll recognise — for example, the company name, bank name, or property address.",
  investment_type: "What kind of investment is this? Pick the category that best describes it — stocks, bonds, property, savings, etc.",
  ticker_symbol: "The short code used to identify a stock on an exchange — for example, AAPL for Apple or GOOGL for Google. Your broker or a quick web search can help you find it.",
  current_value: "How much is this investment worth today? If you're not sure, leave it empty and we'll use the amount you originally invested.",
  current_value_real_estate: "The current market price of the property.",
  coupon_frequency: "How often does this bond pay you interest? Most government bonds pay every 6 months (semi-annual).",
  tenor: "How long until this investment matures or expires — for example, 90 days for a short-term deposit, or 10 years for a long-term bond.",
  currency: "Which currency is this investment denominated in? Choose the currency your broker or bank uses for this particular investment.",
  risk_profile: "How risky is this fund? Low means safer but lower returns. High means potentially bigger gains but also bigger losses.",
  share_price: "The price you paid for each share. If you bought 100 shares at $10 each, enter $10 here.",
  conversion_ratio: "How many shares of the company you'll receive for each unit of your convertible note when it converts. For example, 1.25 means each note converts into 1.25 shares.",
  valuation_cap: "The maximum company value used to calculate your share price when the note converts. A lower cap means you get more shares — it protects early investors.",
  etf_type: "What does this ETF invest in? Equity ETFs hold stocks, Bond ETFs hold bonds, Sector ETFs focus on one industry, and Commodity ETFs track things like gold or oil.",
  current_nav: "Net Asset Value — the current price of one unit of this ETF. You can find this on your broker's app or the fund's website.",
  fund_sponsor: "The company that created and manages the ETF — for example, BlackRock (iShares), Vanguard, or State Street (SPDR).",
  index_tracked: "Which market index does this ETF follow? For example, S&P 500, FTSE 100, or MSCI Emerging Markets.",
};

const FieldTip = ({ field }) => {
  const tip = FIELD_TIPS[field];
  const [open, setOpen] = useState(false);
  if (!tip) return null;

  return (
    <>
      {/* Desktop: hover tooltip */}
      <span className="hidden md:inline-block">
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <HelpCircle className="w-3.5 h-3.5 text-slate-400 hover:text-blue-500 cursor-help inline-block ml-1 -mt-0.5" />
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs text-sm bg-slate-800 text-white p-3 rounded-lg shadow-lg">
              <p>{tip}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </span>

      {/* Mobile: tap to toggle */}
      <span className="inline-block md:hidden relative">
        <HelpCircle
          className="w-3.5 h-3.5 text-slate-400 active:text-blue-500 cursor-pointer inline-block ml-1 -mt-0.5"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(prev => !prev); }}
          onTouchEnd={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(prev => !prev); }}
        />
        {open && (
          <>
            <div className="fixed inset-0 z-9999" onClick={() => setOpen(false)} onTouchEnd={() => setOpen(false)} />
            <div className="fixed left-4 right-4 bottom-24 z-10000 bg-slate-800 text-white text-sm p-4 rounded-xl shadow-2xl border border-slate-700">
              <button
                onClick={(e) => { e.stopPropagation(); setOpen(false); }}
                className="absolute top-2 right-2 text-slate-400 hover:text-white p-1"
              >
                <X className="w-4 h-4" />
              </button>
              <p className="pr-6 leading-relaxed">{tip}</p>
            </div>
          </>
        )}
      </span>
    </>
  );
};

export default FieldTip;
